import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../context/ThemeContext';

function Privacy() {
  const { theme } = useTheme();
  const tokens = theme.tokens;
  const tc = tokens.colors;

  const cardStyle = {
    background: `color-mix(in srgb, ${tc.surface} 75%, transparent)`,
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(6px)',
    border: `1px solid ${tc.gridLine}`,
    borderRadius: 14,
    padding: '32px 36px',
    marginTop: 24,
  };

  const h1Style = {
    fontFamily: tokens.fonts.display,
    fontSize: 'clamp(24px, 5vw, 32px)',
    fontWeight: 600,
    color: tc.ink,
    marginBottom: 20,
    lineHeight: 1.2,
  };

  const h2Style = {
    fontFamily: tokens.fonts.display,
    fontSize: 20,
    fontWeight: 600,
    color: tc.ink,
    marginTop: 28,
    marginBottom: 10,
  };

  const linkStyle = { color: tc.accent };

  return (
    <div className="container mx-auto px-4 sm:px-8 pb-12" style={{ color: tc.muted, fontFamily: tokens.fonts.ui, textAlign: 'left' }}>
      <div style={cardStyle}>
        <Helmet>
          <title>Privacy Policy | ATLTech.events</title>
          <meta name="description" content="Read the ATLTech.events privacy policy — how we collect, use, and protect your information." />
        </Helmet>
        <h1 style={h1Style}>Privacy Policy</h1>

        <div className="space-y-4">
          <p style={{ fontWeight: 600, color: tc.ink }}>Last Updated: April 7, 2025</p>

          <section className="mb-6">
            <h2 style={h2Style}>Information We Collect</h2>
            <p>We collect minimal information to provide you with the best possible experience. This includes:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Browser cache data for improved performance</li>
              <li>Usage analytics to improve our service</li>
              <li>If you contact us through our website or LinkedIn page, we will collect your name, email address, and any other information you provide.</li>
              <li>If you subscribe to our newsletter on Substack, we will collect your name and email address.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 style={h2Style}>How We Use Your Information</h2>
            <p>The information we collect is used solely to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Improve the functionality of our website</li>
              <li>Analyze site usage patterns</li>
              <li>Enhance user experience</li>
              <li>If you contact us through our website or LinkedIn page, we will use your information to respond to your message or provide any services requested.</li>
              <li>If you subscribe to our newsletter on Substack, we will use your email address to send you the newsletter.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 style={h2Style}>Data Storage</h2>
            <p>
              We use local storage to cache event data temporarily on your device to improve performance
              and reduce server load. This data is automatically cleared based on cache expiration rules.
            </p>
          </section>

          <section className="mb-6">
            <h2 style={h2Style}>Tracking Technologies</h2>
            <p>
              We use products from Google and Microsoft to collect usage data. This data is anonymous and cannot be used by us to identify you personally.
            </p>
            <p className="mt-2">
              We use Substack to send our newsletter. Substack is a third-party service that we use to send our newsletter. Please consult their <a href="https://substack.com/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">privacy policy</a> for more information.
            </p>
          </section>

          <section>
            <h2 style={h2Style}>Contact Us</h2>
            <p>
              If you have any questions about our privacy policy, please contact us at <a target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline" href="https://www.linkedin.com/company/atl-tech-events">ATLTech.events<sup>SM</sup> on LinkedIn</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Privacy;
